import { defineComponent } from 'vue'
import Button from 'components/button'
import CardButton from 'components/card-button'
import HeaderSm from 'components/headers/sm'
import Tooltip from 'components/tooltip'
import type { FormWizardFunctionalComponentProps } from 'components/form-wizard/types'
import LinkIcon from 'assets/img/svg/icons/chain-link.svg?url'
import UploadIcon from 'assets/img/svg/icons/upload.svg?url'
// @ts-ignore
import { EAssetType } from 'findef'
import { useResponsive } from '~/composables/useResponsive'
import InfoCard from '../../components/info-card'


const InitialStep = defineComponent<
  FormWizardFunctionalComponentProps & {
    handleClickConnectAccount: () => void;
    handleSetAssetType: (assetType: EAssetType, listed?: boolean) => void;
  }
>({
  setup(props) {

    const responsive = useResponsive();

    const ImportAsset = () => {
      const importCSV = () => {
        console.log('Import CSV')
      }

      return (
        <div class="flex flex-col gap-4">
          <HeaderSm
            title={'Add Assets Automatically'}
            subtitle={
              'Add your assets from other sources either through our integrations or by uploading CSV-files.'
            }
          />
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Button
              icon={{
                src: LinkIcon,
                stroke: 'white',
                size: responsive.isSmall.value ? '1.25rem' : '1.5rem',
                fill: 'none'
              }}
              class="w-full py-0 lg:py-4"
              onClick={() => props.handleClickConnectAccount()}
            >
              Connect Account
            </Button>
            <Tooltip text="Coming Soon">
              <Button
                icon={{
                  src: UploadIcon,
                  stroke: 'currentColor',
                  size: '1rem',
                  fill: 'none'
                }}
                class="w-full py-0 lg:py-4"
                onClick={importCSV}
                variant="white"
                outlined
                disabled
              >
                Upload Files
              </Button>
            </Tooltip>
          </div>
        </div>
      )
    }

    const AssetTypesModal = () => {
      return (
        <div class="flex flex-col gap-2">
          <InfoCard
            variant="gray"
            heightClass='h-auto'
            title="Listed Equity"
            subtitle="Add e.g. listed stocks and funds connected to tickers and automatically track value changes."
            icon={{
              src: async () => await import('assets/img/svg/icons/file-copy2.svg?url'),
              fill: 'currentColor',
              size: 14
            }}
          />
          <InfoCard
            variant="gray"
            heightClass='h-auto'
            title="Unlisted Equity"
            subtitle="Unlisted companies that are not on a public market. Keep your investment updated yourself."
            icon={{
              src: async () => await import('assets/img/svg/icons/rocket.svg?url'),
              fill: 'currentColor',
              size: 14
            }}
          />
          <InfoCard
            variant="gray"
            heightClass='h-auto'
            title="Real Estate"
            subtitle="Add different type of real estate whether it is e.g. your house, apartment or commercial buildings."
            icon={{
              src: async () => await import('assets/img/svg/icons/building.svg?url'),
              fill: 'currentColor',
              size: 14
            }}
          />
          <InfoCard
            variant="gray"
            heightClass='h-auto'
            title="Commodity"
            subtitle="E.g. precious metals, energy resources, and agricultural goods connected to tickers."
            icon={{
              src: async () => await import('assets/img/svg/icons/gold_bars.svg?url'),
              fill: 'currentColor',
              size: 14
            }}
          />
          <InfoCard
            variant="gray"
            heightClass='h-auto'
            title="Crypto"
            subtitle="Add your crypto investments connected to tickers and track their performance in real time."
            icon={{
              src: async () => await import('assets/img/svg/icons/coins.svg?url'),
              fill: 'currentColor',
              size: 14
            }}
          />
          <InfoCard
            variant="gray"
            heightClass='h-auto'
            title="Alternative"
            subtitle="Add your alternative investments. Everything that is of value e.g. art, wine and collectibles."
            icon={{
              src: async () => await import('assets/img/svg/icons/grid-layout.svg?url'),
              fill: 'currentColor',
              size: 14
            }}
          />
          <InfoCard
            variant="gray"
            heightClass='h-auto'
            title="Cash & Savings"
            subtitle="Add your different cash assets from and track your total liquidity."
            icon={{
              src: async () => await import('assets/img/svg/icons/cash-bill.svg?url'),
              fill: 'currentColor',
              size: 14
            }}
          />
          <InfoCard
            variant="gray"
            heightClass='h-auto'
            title="Debt"
            subtitle="Add different type of debt instruments. This enables you to add debt as a lender."
            icon={{
              src: async () => await import('assets/img/svg/icons/receipt-bill.svg?url'),
              fill: 'currentColor',
              size: 14
            }}
          />
        </div>
      )
    }

    const AddManualAsset = () => {
      return (
        <div class="flex flex-col gap-4">
          <div class="flex flex-row"></div>
          <HeaderSm
            title="Add Assets Manually"
            subtitle="Add your assets manually and update your investment data manually over time."
            tooltip={
              responsive.isSmall.value
              ? {
                title: 'Asset Class Details',
                render: () => <AssetTypesModal />,
                fullscreen: responsive.isSmall.value
              }
              : undefined
            }
          />
          <div class="grid grid-cols-2 grid-rows-2 gap-4">
            <CardButton
              icon={{
                src: async () => await import('assets/img/svg/icons/file-copy2.svg?url'),
                fill: 'currentColor',
                size: responsive.isSmall.value ? '1.25rem' : '2rem'
              }}
              title="Listed Equity"
              subtitle={responsive.isSmall.value ? undefined : "Add e.g. listed stocks and funds connected to tickers and automatically track value changes."}
              click={() => {
                props.handleSetAssetType(EAssetType.EQUITY, true)
                if (props.next) {
                  props.next()
                }
              }}
            />
            <CardButton
              icon={{
                src: async () => import('assets/img/svg/icons/rocket2.svg?raw'),
                fill: 'currentColor',
                size: responsive.isSmall.value ? '1.25rem' : '2rem'
              }}
              title="Unlisted Equity"
              subtitle={responsive.isSmall.value ? undefined : "Unlisted companies that are not on a public market. Keep your investment updated yourself."}
              click={() => {
                props.handleSetAssetType(EAssetType.EQUITY, false)
                if (props.next) {
                  props.next()
                }
              }}
            />
            <CardButton
              icon={{
                src: async () => await import('assets/img/svg/icons/building.svg?url'),
                fill: 'currentColor',
                size: responsive.isSmall.value ? '1.25rem' : '2rem'
              }}
              title="Real Estate"
              subtitle={responsive.isSmall.value ? undefined : "Add your different type of real estate whether it is e.g. your house, apartment or commercial buildings."}
              click={() => {
                props.handleSetAssetType(EAssetType.REAL_ESTATE)
                if (props.next) {
                  props.next()
                }
              }}
            />
            <CardButton
              icon={{
                src: async () => import('assets/img/svg/icons/gold_bars.svg?raw'),
                fill: 'currentColor',
                size: responsive.isSmall.value ? '1.25rem' : '2rem'
              }}
              title="Commodity"
              subtitle={responsive.isSmall.value ? undefined : "Add  e.g. precious metals, energy resources, and agricultural goods connected to tickers."}
              click={() => {
                props.handleSetAssetType(EAssetType.COMMODITY, true)
                if (props.next) {
                  props.next()
                }
              }}
            />
            <CardButton
              icon={{
                src: async () => import('assets/img/svg/icons/coins.svg?raw'),
                fill: 'currentColor',
                size: responsive.isSmall.value ? '1.25rem' : '2rem'
              }}
              title="Crypto"
              subtitle={responsive.isSmall.value ? undefined : "Add your crypto investments connected to tickers and track their performance in real time."}
              click={() => {
                props.handleSetAssetType(EAssetType.CRYPTO)
                if (props.next) {
                  props.next()
                }
              }}
            />
            <CardButton
              icon={{
                src: async () => import('assets/img/svg/icons/grid.svg?raw'),
                fill: 'currentColor',
                size: responsive.isSmall.value ? '1.25rem' : '2rem'
              }}
              title="Alternative"
              subtitle={responsive.isSmall.value ? undefined : "Add your alternative investments. Everything you own that is of value e.g. art, wine and collectibles."}
              click={() => {
                props.handleSetAssetType(EAssetType.ALTERNATIVE)
                if (props.next) {
                  props.next()
                }
              }}
            />
            <CardButton
              icon={{
                src: async () => import('assets/img/svg/icons/cash-bill.svg?raw'),
                fill: 'currentColor',
                size: responsive.isSmall.value ? '1.25rem' : '2rem'
              }}
              title="Cash & Savings"
              subtitle={responsive.isSmall.value ? undefined : "Add your different cash deposits either as physical cash or as placements in a savings account with interest."}
              click={() => {
                props.handleSetAssetType(EAssetType.CASH)
                if (props.next) {
                  props.next()
                }
              }}
            />
            <Tooltip text="Coming soon">
              <CardButton
                class="w-full"
                disabled
                icon={{
                  src: async () => import('assets/img/svg/icons/receipt-bill.svg?raw'),
                  fill: 'currentColor',
                  size: responsive.isSmall.value ? '1.25rem' : '2rem'
                }}
                title="Debt"
                subtitle={responsive.isSmall.value ? undefined : "Add different type of debt instruments. This enables you to add debt as a lender."}
              />
            </Tooltip>
          </div>
        </div>
      )
    }

    return () => {
      return (
        <div class="flex flex-col w-full">
          <div class="flex flex-col flex-1 items-center justify-center w-full px-4 lg:px-12">
            <div class="flex flex-col flex-1 max-w-4xl gap-14 overflow-visible">
              <ImportAsset />
              <AddManualAsset />
            </div>
          </div>
        </div>
      )
    }
  }
})

InitialStep.props = [
  'manage',
  'handleClickConnectAccount',
  'handleSetAssetType'
]

export default InitialStep
